import config from '../config/index.js';
import { setupAuthentication } from './auth.js';
import { getUser, fetchCardData } from './data/update_operations.js';
import {
  handleAuthenticatedUser as navbarHandleAuthenticatedUser,
  handleUnauthenticatedUser as navbarHandleUnauthenticatedUser,
} from './navbar.js';
import { shareLink } from './utils/share_utils.js';
import { CardDisplayPopupController } from './controllers/popups/card_display_popup_controller.js';

let userToken = null;
let loggedInUsername = null;
let pendingUsername = null;
let pendingCollectionId = null;
const collectionDataMap = {};

function escapeHtml(text) {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function formatBio(bio) {
  if (!bio) return '';
  const urlRegex = /((?:https?:\/\/|www\.)[^\s<]+)/gi;
  const escaped = escapeHtml(bio);
  const linked = escaped.replace(urlRegex, (match) => {
    let cleanMatch = match;
    let trailing = '';

    while (cleanMatch.length > 0 && /[),.!?:;'"\]]$/.test(cleanMatch)) {
      trailing = cleanMatch.slice(-1) + trailing;
      cleanMatch = cleanMatch.slice(0, -1);
    }

    if (cleanMatch.length === 0) {
      cleanMatch = match;
      trailing = '';
    }

    let href = cleanMatch;
    if (!/^https?:\/\//i.test(cleanMatch)) {
      href = `https://${cleanMatch}`;
    }
    return `<a href="${href}" target="_blank" rel="noopener noreferrer">${cleanMatch}</a>${trailing}`;
  });

  return linked.replace(/\n/g, '<br />');
}

function makeHeader() {
  return userToken ? { Authorization: `Bearer ${userToken}` } : {};
}

async function saveCustomDescription(cardId, collectionId, newDescription) {
  try {
    const headers = makeHeader();
    headers['Content-Type'] = 'application/json';
    await fetch(`${config.detectionUrl}/save-card-description`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        card_id: cardId,
        collection_id: collectionId,
        description: newDescription,
      }),
    });
  } catch (error) {
    console.error('Error in saveCustomDescription:', error);
  }
}

function showLoginPrompt() {
  const backdrop = document.createElement('div');
  backdrop.classList.add('login-prompt-backdrop');

  const modal = document.createElement('div');
  modal.classList.add('login-prompt-modal');

  const header = document.createElement('div');
  header.classList.add('login-prompt-header');
  const headerText = document.createElement('h2');
  headerText.textContent = 'Login Required';
  header.appendChild(headerText);

  const closeButton = document.createElement('button');
  closeButton.classList.add('login-prompt-close');
  closeButton.innerHTML = '×';
  closeButton.addEventListener('click', () => {
    document.body.removeChild(backdrop);
  });
  header.appendChild(closeButton);

  const body = document.createElement('div');
  body.classList.add('login-prompt-body');
  const message = document.createElement('p');
  message.textContent =
    'Unlock the full power of CardSense to track, share, and trade your cards. ' +
    'Log in or create a free account now to get started!';
  body.appendChild(message);

  const footer = document.createElement('div');
  footer.classList.add('login-prompt-footer');

  const currentUrl = window.location.href;

  const loginLink = document.createElement('a');
  loginLink.href = `/login.html?redirect=${encodeURIComponent(currentUrl)}`;
  loginLink.textContent = 'Log In';
  loginLink.classList.add('login-btn-link');

  const orSpan = document.createElement('span');
  orSpan.textContent = 'or';

  const signupLink = document.createElement('a');
  signupLink.href = `/signup.html?redirect=${encodeURIComponent(currentUrl)}`;
  signupLink.textContent = 'Sign Up';
  signupLink.classList.add('login-btn-link');

  footer.appendChild(loginLink);
  footer.appendChild(orSpan);
  footer.appendChild(signupLink);

  modal.appendChild(header);
  modal.appendChild(body);
  modal.appendChild(footer);

  backdrop.addEventListener('click', (event) => {
    if (event.target === backdrop) {
      document.body.removeChild(backdrop);
    }
  });

  backdrop.appendChild(modal);
  document.body.appendChild(backdrop);
}

function getCardTitle(reference_card) {
  let title = `${reference_card.year} ${reference_card.program} ${reference_card.release_set} ${reference_card.players}`;
  if (reference_card.parallel) {
    title += ': ' + reference_card.parallel;
  }
  return title;
}

async function postMessageToBackend(cardId, collectionId, message) {
  const headers = makeHeader();
  const newHeaders = { ...headers, 'Content-Type': 'application/json' };
  const payload = { card_id: cardId, collection_id: collectionId, message };

  const res = await fetchCardData(
    `${config.detectionUrl}/post-message`,
    'POST',
    newHeaders,
    payload
  );

  return res.body;
}

function showMessageModal(isLoggedIn, cardInfo, collectionId) {
  const modalBackdrop = document.createElement('div');
  modalBackdrop.classList.add('message-modal-backdrop');

  const modalContainer = document.createElement('div');
  modalContainer.classList.add('message-modal-container');

  const modalHeader = document.createElement('div');
  modalHeader.classList.add('message-modal-header');

  const modalTitle = document.createElement('h2');
  modalTitle.textContent = 'Send a Message';
  modalHeader.appendChild(modalTitle);

  const closeButton = document.createElement('button');
  closeButton.classList.add('message-modal-close');
  closeButton.innerHTML = '×';
  closeButton.addEventListener('click', () => {
    document.body.removeChild(modalBackdrop);
  });
  modalHeader.appendChild(closeButton);

  const modalBody = document.createElement('div');
  modalBody.classList.add('message-modal-body');

  const cardInfoDiv = document.createElement('div');
  cardInfoDiv.classList.add('message-modal-card-info');
  cardInfoDiv.innerHTML = `
    Messaging about ${getCardTitle(cardInfo.reference_card)}
    ${cardInfo.reference_card.name ? `(${cardInfo.reference_card.name})` : ''}
  `;
  modalBody.appendChild(cardInfoDiv);

  const messageTextarea = document.createElement('textarea');
  messageTextarea.name = 'messageBody';
  messageTextarea.placeholder = 'Type your message here...';
  modalBody.appendChild(messageTextarea);

  const modalFooter = document.createElement('div');
  modalFooter.classList.add('message-modal-footer');

  const sendButton = document.createElement('button');
  sendButton.classList.add('message-modal-send-btn');
  sendButton.textContent = 'Send';

  sendButton.addEventListener('click', async () => {
    const messageText = messageTextarea.value.trim();

    if (!messageText) {
      alert('Please enter a message.');
      return;
    }

    try {
      if (!isLoggedIn) {
        showLoginPrompt();
        return;
      }

      await postMessageToBackend(
        cardInfo.user_card.user_card_id,
        collectionId,
        messageText
      );

      alert('Message sent successfully!');
      document.body.removeChild(modalBackdrop);
    } catch (err) {
      console.error('Error sending message:', err);
      alert('Something went wrong. Please try again.');
    }
  });

  modalFooter.appendChild(sendButton);

  modalContainer.appendChild(modalHeader);
  modalContainer.appendChild(modalBody);
  modalContainer.appendChild(modalFooter);

  modalBackdrop.appendChild(modalContainer);

  modalBackdrop.addEventListener('click', (event) => {
    if (event.target === modalBackdrop) {
      document.body.removeChild(modalBackdrop);
    }
  });

  document.body.appendChild(modalBackdrop);
  messageTextarea.focus();
}

function createDescriptionSnippet(fullDescription) {
  const snippetContainer = document.createElement('div');
  snippetContainer.classList.add('description-snippet');
  const maxChars = 100;
  if (fullDescription.length <= maxChars) {
    snippetContainer.textContent = fullDescription;
  } else {
    const shortDescription = fullDescription.substring(0, maxChars) + '... ';
    snippetContainer.textContent = shortDescription;
    const readMoreLink = document.createElement('a');
    readMoreLink.href = '#';
    readMoreLink.textContent = 'Read more';
    readMoreLink.addEventListener('click', (event) => {
      event.stopPropagation();
      event.preventDefault();
      snippetContainer.textContent = fullDescription;
    });
    snippetContainer.appendChild(readMoreLink);
  }
  return snippetContainer;
}

function createDescriptionArea(existingDesc, collectionId, cardId) {
  const container = document.createElement('div');
  container.classList.add('description-container');
  const placeholder = document.createElement('div');
  placeholder.classList.add('description-placeholder');
  placeholder.textContent =
    existingDesc && existingDesc.trim().length > 0
      ? existingDesc
      : 'Set Description';
  const input = document.createElement('input');
  input.type = 'text';
  input.classList.add('description-input');
  input.value = existingDesc || '';
  input.style.display = 'none';
  container.appendChild(placeholder);
  container.appendChild(input);
  placeholder.addEventListener('click', (event) => {
    event.stopPropagation();
    placeholder.style.display = 'none';
    input.style.display = 'inline-block';
    input.focus();
  });
  input.addEventListener('keydown', async (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      await commitDescription();
    }
  });
  input.addEventListener('blur', async () => {
    await commitDescription();
  });
  async function commitDescription() {
    const newDesc = input.value.trim();
    await saveCustomDescription(cardId, collectionId, newDesc);
    placeholder.textContent = newDesc.length > 0 ? newDesc : 'Set Description';
    input.style.display = 'none';
    placeholder.style.display = 'inline-block';
  }
  return container;
}

function getImage(imgElement, imageUrl, altText) {
  fetch(imageUrl)
    .then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.blob();
    })
    .then((blob) => {
      const localUrl = URL.createObjectURL(blob);
      imgElement.src = localUrl;
      imgElement.alt = altText;
    })
    .catch((error) => console.error('Error fetching the image:', error));
}

function createImageElement(cardInfo) {
  const img = document.createElement('img');
  getImage(
    img,
    cardInfo.user_card.front_image_url_medium,
    cardInfo.reference_card.identifier
  );
  return img;
}

function createTitleElement(title) {
  const statusDiv = document.createElement('div');
  statusDiv.classList.add('card-title');
  statusDiv.textContent = title;
  return statusDiv;
}

function createSubtitleElement(subtitle) {
  const statusDiv = document.createElement('div');
  statusDiv.classList.add('card-subtitle');
  statusDiv.textContent = subtitle ? subtitle : 'Unknown Team';
  return statusDiv;
}

function createCardElement(
  cardInfo,
  isLoggedIn,
  isOwner,
  collectionId
) {
  const cardDiv = document.createElement('div');
  cardDiv.classList.add('card');
  if (!isOwner) {
    cardDiv.classList.add('card--public-view');
  }
  cardDiv.addEventListener('click', () => {
    const controller = new CardDisplayPopupController(null, cardInfo, true);
    controller.showCardDisplayPopup([], []);
  });
  const titleContentContainer = document.createElement('div');
  titleContentContainer.classList.add('title-content-container');
  const titleText =
    cardInfo.reference_card.players[0] +
    ' - ' +
    cardInfo.reference_card.year +
    ' ' +
    cardInfo.reference_card.program;
  let titleText2 = cardInfo.reference_card.release_set;
  if (cardInfo.reference_card.parallel) {
    titleText2 += ` - ${cardInfo.reference_card.parallel}`;
  }
  const teamToShow = cardInfo?.user_card?.team_override || '';
  const positionToShow = cardInfo?.user_card?.position_override || '';
  const teamLabel = teamToShow || (positionToShow ? 'Unknown Team' : '');
  const subtitleText = positionToShow
    ? `${teamLabel} - ${positionToShow}`
    : teamLabel;
  const title = createTitleElement(titleText);
  const title2 = createTitleElement(titleText2);
  const subtitle = createSubtitleElement(subtitleText);
  if (titleText2) {
    titleContentContainer.appendChild(title);
    titleContentContainer.appendChild(title2);
  } else {
    titleContentContainer.appendChild(title2);
    titleContentContainer.appendChild(title);
  }
  titleContentContainer.appendChild(subtitle);
  cardDiv.appendChild(titleContentContainer);
  const contentContainer = document.createElement('div');
  contentContainer.classList.add('card-content-container');
  const img = createImageElement(cardInfo);
  contentContainer.appendChild(img);
  const descMsgContainer = document.createElement('div');
  descMsgContainer.classList.add('desc-msg-container');

  const descContainer = document.createElement('div');
  descContainer.classList.add('desc-container');

  const userCard = cardInfo.user_card || {};
  const existingDesc = userCard.description || '';

  const searchTerms = [
    cardInfo.reference_card.players.join(' '),
    cardInfo.reference_card.year,
    cardInfo.reference_card.program,
    cardInfo.reference_card.release_set,
    cardInfo.reference_card.parallel || '',
    cardInfo.reference_card.number || '',
    teamToShow,
    positionToShow,
    existingDesc || '',
  ]
    .join(' ')
    .toLowerCase();
  cardDiv.dataset.searchable = searchTerms;

  if (isOwner) {
    const descArea = createDescriptionArea(
      existingDesc,
      collectionId,
      userCard.user_card_id
    );
    descContainer.appendChild(descArea);
  } else {
    if (existingDesc.trim().length > 0) {
      const snippet = createDescriptionSnippet(existingDesc);
      descContainer.appendChild(snippet);
    }
  }

  descMsgContainer.appendChild(descContainer);

  const messageIconBtn = document.createElement('button');
  messageIconBtn.classList.add('message-icon-btn');
  messageIconBtn.innerHTML = '<i class="fas fa-comment"></i>';

  if (isOwner) {
    messageIconBtn.style.display = 'none';
  }

  messageIconBtn.addEventListener('click', (event) => {
    event.stopPropagation();
    if (!isLoggedIn) {
      showLoginPrompt();
      return;
    }
    showMessageModal(isLoggedIn, cardInfo, collectionId);
  });

  descMsgContainer.appendChild(messageIconBtn);

  contentContainer.appendChild(descMsgContainer);
  cardDiv.appendChild(contentContainer);
  return cardDiv;
}

function addCardsToGrid(grid, data, isLoggedIn, isOwner, collectionId) {
  const cards = data.cards || [];
  // Sort cards by user card id so the most recently added card appears first
  cards.sort((a, b) => {
    const idA =
      a.user_card.user_card_id !== undefined
        ? a.user_card.user_card_id
        : a.user_card.id;
    const idB =
      b.user_card.user_card_id !== undefined
        ? b.user_card.user_card_id
        : b.user_card.id;
    return idB - idA;
  });
  cards.forEach((card) => {
    const cardElement = createCardElement(
      card,
      isLoggedIn,
      isOwner,
      collectionId
    );
    grid.appendChild(cardElement);
  });
  const searchInput = document.getElementById('cardSearch');
  if (searchInput) {
    filterCards(searchInput.value);
  }
}

function filterCards(query) {
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  document.querySelectorAll('.card').forEach((el) => {
    const data = el.dataset.searchable || '';
    const visible = terms.every((t) => data.includes(t));
    el.style.display = visible ? '' : 'none';
  });
}

function setupSearchFilter() {
  const searchInput = document.getElementById('cardSearch');
  if (!searchInput) return;
  searchInput.addEventListener('input', (e) => {
    filterCards(e.target.value);
  });
}

function setupCollectionTabs(
  publicCollections,
  isLoggedIn,
  isOwner,
  initialCollectionId,
  profileUsername
) {
  const container = document.getElementById('collection-tabs');
  const grid = document.querySelector('.card-grid');
  if (!container || !grid) return;
  container.innerHTML = '';

  const validCollections = publicCollections.filter((col) => {
    const data = collectionDataMap[col.id];
    return data && Array.isArray(data.cards) && data.cards.length > 0;
  });

  const createTab = (collection, isActive) => {
    const btn = document.createElement('button');
    btn.className = 'collection-tab';
    btn.dataset.collectionId = collection.id;

    if (isOwner) {
      btn.innerHTML = `${collection.name} <i class="copy-link fas fa-arrow-up-right-from-square" title="Share Link"></i>`;
      const copyIcon = btn.querySelector('.copy-link');
      copyIcon.addEventListener('click', (e) => {
        e.stopPropagation();
        const link = `${window.location.origin}/profile.html?username=${profileUsername}&collection=${collection.id}`;
        const title = `${profileUsername}'s CardSense collection`;
        shareLink(
          link,
          () => alert('Link ready to share'),
          () => alert('Failed to share link'),
          title
        );
      });
    } else {
      btn.textContent = collection.name;
    }

    if (isActive) btn.classList.add('active');

    btn.addEventListener('click', () => {
      document
        .querySelectorAll('.collection-tab')
        .forEach((t) => t.classList.remove('active'));
      btn.classList.add('active');
      grid.innerHTML = '';
      const data = collectionDataMap[collection.id];
      if (data) addCardsToGrid(grid, data, isLoggedIn, isOwner, collection.id);
    });
    return btn;
  };

  let activeId = null;
  if (
    initialCollectionId &&
    validCollections.some((c) => String(c.id) === String(initialCollectionId))
  ) {
    activeId = Number(initialCollectionId);
  } else if (validCollections.length > 0) {
    activeId = validCollections[0].id;
  }

  validCollections.forEach((col) => {
    container.appendChild(createTab(col, col.id === activeId));
  });

  if (validCollections.length > 0) {
    grid.innerHTML = '';
    const data = collectionDataMap[activeId];
    if (data) addCardsToGrid(grid, data, isLoggedIn, isOwner, activeId);
  } else {
    grid.innerHTML = '<div class="default-text">No public collections.</div>';
  }
}

async function displayProfilePic(profilePicId) {
  try {
    const downloadUrl = `${config.detectionUrl}/uploads/${profilePicId}.jpg`;
    const picRes = await fetch(downloadUrl);
    if (!picRes.ok) return;
    const picBlob = await picRes.blob();
    const picURL = URL.createObjectURL(picBlob);
    const photoContainer = document.getElementById('collection-user-photo');
    if (!photoContainer) return;
    const img = document.createElement('img');
    img.src = picURL;
    img.alt = 'Profile Picture';
    img.classList.add('user-avatar-large');
    photoContainer.appendChild(img);
  } catch (err) {
    console.error('Error downloading profile picture:', err);
  }
}

async function showPublicProfile(username, isLoggedIn, initialCollectionId) {
  const app = document.getElementById('app');
  Object.keys(collectionDataMap).forEach((k) => delete collectionDataMap[k]);
  try {
    const response = await fetch(
      `${config.detectionUrl}/public-profile/${username}`,
      { method: 'GET' }
    );
    const data = await response.json();
    if (!response.ok) {
      app.innerHTML = `<h2>${data.error || 'Unable to load profile'}</h2>`;
      return;
    }
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle)
      ogTitle.setAttribute(
        'content',
        `${data.username}'s CardSense collection`
      );
    let html = `<div class="public-profile">`;
    html += `  <div class="public-profile__header">`;
    html += `    <div id="collection-user-photo" class="public-profile__photo"></div>`;
    html += `    <div class="public-profile__user-info">`;
    html += `      <p class="public-profile__username">${data.username}`;
    if (data.subscription_level === 'pro') {
      html += ` <span class="account-tag pro">Pro</span>`;
    } else if (data.subscription_level === 'lifetime') {
      html += ` <span class="account-tag pro">Pro</span>`;
      html += ` <span class="account-tag lifetime">Founding Member</span>`;
    }
    html += `      </p>`;
    if (data.lifetime_number) {
      html += `      <p class="public-profile__lifetime-number">Founding Member Number: <strong>${data.lifetime_number}/149</strong></p>`;
    }
    html += `    </div>`;
    html += `  </div>`;
    html += `  <div class="public-profile__details">`;
    if (data.bio && data.bio.trim().length > 0) {
      html += `    <p class="public-profile__bio">${formatBio(
        data.bio
      )}</p>`;
    } else {
      html += `    <p class="public-profile__bio">No bio provided.</p>`;
    }
    html += `  </div>`;
    html += `</div>`;
    html += `<div class="collection-wrapper">`;
    html += `  <div class="collection-tabs" id="collection-tabs"></div>`;
    html += `  <div class="collection-frame">`;
    html += `  <div class="card-search-container">`;
    html += `    <input id="cardSearch" type="text" placeholder="Search cards…" aria-label="Search cards" />`;
    html += `  </div>`;
    html += `  <div class="card-grid"></div>`;
    html += `  </div>`;
    html += `</div>`;
    app.innerHTML = html;
    setupSearchFilter();
    if (data.profile_pic_id) displayProfilePic(data.profile_pic_id);
    const isOwner = isLoggedIn && loggedInUsername === username;

    const collectionsToShow = data.public_collections || [];

    if (collectionsToShow.length > 0) {
      const colPromises = collectionsToShow.map((col) =>
        fetch(`${config.detectionUrl}/collections/${col.id}`).then((r) =>
          r.json()
        )
      );
      const collections = await Promise.all(colPromises);
      const validCollections = [];
      collections.forEach((colData, idx) => {
        const colId = collectionsToShow[idx].id;
        collectionDataMap[colId] = colData;
        if (
          colData &&
          Array.isArray(colData.cards) &&
          colData.cards.length > 0
        ) {
          validCollections.push(collectionsToShow[idx]);
        }
      });
      setupCollectionTabs(
        validCollections,
        isLoggedIn,
        isOwner,
        initialCollectionId,
        username
      );
    } else {
      setupCollectionTabs(
        [],
        isLoggedIn,
        isOwner,
        initialCollectionId,
        username
      );
    }
  } catch (error) {
    console.error(error);
    app.innerHTML = '<h2>Error loading profile</h2>';
  }
}

async function handleAuthenticatedUser(user, token) {
  navbarHandleAuthenticatedUser(user, token);
  userToken = token;
  try {
    const serverUser = await getUser(makeHeader());
    loggedInUsername = serverUser.username;
  } catch (err) {
    console.error('Error fetching user:', err);
  }
  if (pendingUsername) {
    showPublicProfile(pendingUsername, true, pendingCollectionId);
    pendingUsername = null;
    pendingCollectionId = null;
  }
}

function handleUnauthenticatedUser() {
  navbarHandleUnauthenticatedUser();
  userToken = null;
  loggedInUsername = null;
  if (pendingUsername) {
    showPublicProfile(pendingUsername, false, pendingCollectionId);
    pendingUsername = null;
    pendingCollectionId = null;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const params = new URLSearchParams(window.location.search);
  pendingUsername = params.get('username');
  pendingCollectionId = params.get('collection');
  if (pendingUsername) {
    showPublicProfile(pendingUsername, false, pendingCollectionId);
  }
  setupAuthentication(handleAuthenticatedUser, handleUnauthenticatedUser);
});
